import Vue from "vue";
import App from "./App.vue";
import { router } from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import "./assets/css/styles.scss";
import { i18n } from "./i18n";
import dayjs from "dayjs";
import dayjsUtc from "dayjs/plugin/utc";
import dayjsTimeZone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import VueMask from "v-mask";
import { ApmVuePlugin } from "@elastic/apm-rum-vue";
import "./directives";
import fixedColumnsTableMixin from "./mixins/fixedColumnsTable.mixin";
import { DateUtil } from "./utils/date.util";
import ConfigurationService from "./services/configuration/configuration.service";

declare module "vue/types/vue" {
	interface Vue {
		$formatDate: (value: string | undefined, format: string) => string;
		$formatDateRaw: (value: string | undefined, format: string) => string;
		$translateWithFallback: (path: string, field: string, parameters?: Record<string, string>) => string;
	}
}

dayjs.extend(customParseFormat);
dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimeZone);

const isDevEnv = process.env.NODE_ENV === "development" || process.env.VUE_APP_NODE_ENV_LOCAL === "true";
const apmActive = process.env.VUE_APP_APM_ACTIVE === "true";
const tz = process.env.VUE_APP_TIMEZONE ?? "Europe/Madrid";

dayjs.tz.setDefault(tz);

Vue.config.productionTip = false;
Vue.config.devtools = true;

Vue.use(VueMask);

Vue.directive("uppercase", {
	bind(el: any) {
		const input = el.querySelector("input");

		if (!input) {
			if (el.innerText) {
				el.innerText = el.innerText.toUpperCase();
			}
		}
	},
	update(el: any) {
		const input = el.querySelector("input");

		if (input) {
			const sourceValue = input.value;
			const newValue = sourceValue.toUpperCase();

			if (sourceValue !== newValue) {
				input.value = newValue;
				input.dispatchEvent(new Event("input", { bubbles: true }));
			}
		}
	}
});

Vue.mixin(fixedColumnsTableMixin);
Vue.mixin({
	methods: {
		$formatDate: (value: string, format: string): string => {
			if (!value) return "";

			return DateUtil.formatTimezoneDate({ value, format });
		},
		$formatDateRaw: (value: string, format: string): string => {
			if (!value) return "";

			return DateUtil.formatDate({ value, format });
		},
		$translateWithFallback: (path: string, key: string, parameters?: Record<string, string>): string => {
			const fullPath = [path, key].join(".");
			if (i18n.te(fullPath)) {
				return i18n.t(fullPath, parameters).toString();
			}

			if (i18n.te(key)) {
				return i18n.t(key, parameters).toString();
			}

			return key;
		}
	}
});

// check deploy in progress
ConfigurationService.load().then(() => {
	const { deployInProgress } = ConfigurationService.value;

	const $store = store as any;
	$store.commit("checkAuth");

	if (deployInProgress) {
		$store.dispatch("logout").then(() => initApp());
	} else {
		if ($store.state?.user?.logged) {
			$store.dispatch("loadPermissions").then((success: boolean) => {
				if (!success) {
					$store.dispatch("logout").then(() => initApp());
				} else {
					initApp();
				}
			});
		} else {
			initApp();
		}
	}
});

async function initApp() {
	if (process.env.VUE_APP_APM_SERVER_URL !== "") {
		Vue.use(ApmVuePlugin, {
			router,
			config: {
				serviceName: process.env.VUE_APP_APM_SERVICE_NAME,
				environment: process.env.VUE_APP_APM_ENV,
				serverUrl: process.env.VUE_APP_APM_SERVER_URL,
				active: !isDevEnv && apmActive
			}
		});
	}

	new Vue({
		router,
		store,
		vuetify,
		i18n,
		render: (h) => h(App),
		provide() {
			return {
				$dayjs: dayjs
			};
		}
	}).$mount("#app");
}
